import { ptBR } from '@mui/material/locale';
import { createTheme } from '@mui/material/styles';

const ProduttivoMuiTheme = createTheme(
  {
    palette: {
      background: {
        default: '#FFF',
        header: '#FAFAFA',
      },
      text: {
        primary: '#212121',
        secondary: '#616161',
        disabled: '#9E9E9E',
      },
      primary: {
        main: '#018923',
        dark: '#005A00',
        light: '#23A937',
        contrastText: '#FFFFFF',
        hover: '#0189230A',
        selected: '#01892314',
        focus: '#0189231F',
        focusVisible: '#0189234D',
        outlinedBorder: '#0189234D',
      },
      success: {
        main: '#007817',
        contrastText: '#FFF',
      },
      warning: {
        main: '#EF6C00',
      },
      info: {
        main: '#0288D1',
      },
      error: {
        main: '#C62828',
      },
      divider: '#0000001F',
      // default: {
      //     main: '#FFF',
      // },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h1: { fontSize: '6rem', fontWeight: 700, lineHeight: '116.7%', letterSpacing: '-0.09rem' },
      h2: { fontSize: '3.75rem', fontWeight: 700, lineHeight: '120%', letterSpacing: '-0.03rem' },
      h3: { fontSize: '3rem', fontWeight: 700, lineHeight: '116.7%' },
      h4: { fontSize: '2.125rem', fontWeight: 700, lineHeight: '123.5%', letterSpacing: '0.015rem' },
      h5: { fontSize: '1.5rem', fontWeight: 700, lineHeight: '133.4%' },
      h6: { fontSize: '1.25rem', fontWeight: 700, lineHeight: '160%', letterSpacing: '0.009rem' },
      subtitle1: { fontSize: '1rem', fontWeight: 400, lineHeight: '175%', letterSpacing: '0.009rem' },
      subtitle2: { fontSize: '0.875rem', fontWeight: 500, lineHeight: '157%', letterSpacing: '0.006rem' },
      body1: { fontSize: '1rem', fontWeight: 400, lineHeight: '150%', letterSpacing: '0.009rem' },
      body2: { fontSize: '0.875rem', fontWeight: 400, lineHeight: '143%', letterSpacing: '0.010rem' },
      caption: { fontSize: '0.75rem', fontWeight: 400, lineHeight: '166%', letterSpacing: '0.025rem' },
      overline: { fontSize: '0.75rem', fontWeight: 400, lineHeight: '266%', letterSpacing: '0.0625rem' },
    },
    spacing: 4,
    shape: {
      none: 0,
      borderRadius1: 4,
      borderRadius2: 8,
      borderRadius3: 16,
      borderRadius4: 24,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            '&.MuiButton-outlinedSuccess': {
              borderColor: '#01892380',
            },
          },
          sizeMedium: {
            color: '#FFF',
            padding: '0.5rem 1.375rem',
            backgroundColor: '#018923',
            borderRadius: '0.25rem',
            minWidth: 'auto',
            maxWidth: '16rem',
            width: 'auto',
            '&:hover': {
              backgroundColor: '#01A32D',
            },
            '&.Mui-disabled': {
              backgroundColor: '#0000001F',
              color: '#00000061',
            },
          },
          sizeLarge: {
            color: '#FFF',
            padding: '0.5rem 1.375rem', 
            backgroundColor: '#018923',
            borderRadius: '0.25rem',
            width: 'auto',
            fontSize: '1.125rem', 
            '&:hover': {
              backgroundColor: '#01A32D',
            },
            '&.Mui-disabled': {
              backgroundColor: '#0000001F',
              color: '#00000061',
              border: 'none',
            },
          },
        },
        variants: [
          {
            props: { variant: 'transparent' },
            style: {
              backgroundColor: 'transparent',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: 'transparent',
                boxShadow: 'none',
              },
              '&:active': {
                backgroundColor: 'backgroundColor: rgba(0, 0, 0, 0.1)',
                boxShadow: 'none',
              },
            },
          },
          {
            props: { variant: 'transparentErrorButton' },
            style: {
              backgroundColor: 'transparent',
              color: '#5F2120',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: 'transparent',
                boxShadow: 'none',
              },
              '&:active': {
                backgroundColor: 'backgroundColor: rgba(0, 0, 0, 0.1)',
                boxShadow: 'none',
              },
            },
          },
          {
            props: { variant: 'outlined' },
            style: {
              backgroundColor: 'transparent',
              boxShadow: 'none',
              color: '#018923',
              '&:hover': {
                backgroundColor: 'transparent',
                boxShadow: 'none',
              },
              '&:active': {
                backgroundColor: 'backgroundColor: rgba(0, 0, 0, 0.1)',
                boxShadow: 'none',
              },
            },
          },
          {
            props: { variant: 'cancelDialog' },
            style: {
              backgroundColor: 'transparent',
              boxShadow: 'none',
              color: '#212121',
              '&:hover': {
                backgroundColor: 'transparent',
                boxShadow: 'none',
              },
              '&:active': {
                backgroundColor: 'backgroundColor: rgba(0, 0, 0, 0.1)',
                boxShadow: 'none',
              },
            },
          },
          {
            props: { variant: 'redOutlined' },
            style: {
              backgroundColor: 'transparent',
              boxShadow: 'none',
              color: '#C62828',
              border: '1px solid #C62828',
              borderRadius: '0.25rem',
              '&:hover': {
                backgroundColor: 'transparent',
                boxShadow: 'none',
              },
              '&:active': {
                backgroundColor: 'backgroundColor: rgba(0, 0, 0, 0.1)',
                boxShadow: 'none',
              },
              '&:disabled': {
                backgroundColor: 'transparent',
                border: '1px solid #9E9E9E',
                color: '#9E9E9E'
              },
            },
          },
          {
            props: { variant: 'redButton' },
            style: {
              backgroundColor: '#C62828',
              boxShadow: 'none',
              color: '#FFF',
              '&:hover': {
                backgroundColor: '#C62828',
                boxShadow: 'none',
              },
              '&:active': {
                backgroundColor: 'backgroundColor: rgba(0, 0, 0, 0.1)',
                boxShadow: 'none',
              },
            },
          },
        ],
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: '12px',
            lineHeight: '140%',
          },
        },
      },
      MuiBox: {
        styleOverrides: {
          root: {
            backgroundColor: '#fff',
          },
        },
      },
      MuiTablePagination: {
        styleOverrides: {
          toolbar: {
            alignItems: 'baseline',
          },
        },
      },
      MuiChip: {
        variants: [
          {
            props: { color: 'success' },
            style: {
              color: '#FFF',
              backgroundColor: '#007817',
            },
          },
          {
            props: { color: 'warning' },
            style: {
              color: '#FFF',
              backgroundColor: '#EF6C00',
            },
          },
          {
            props: { color: 'error' },
            style: {
              color: '#FFF',
              backgroundColor: '#C62828',
            },
          },
          {
            props: { color: 'info' },
            style: {
              color: '#FFF',
              backgroundColor: '#0288D1',
            },
          },
          {
            props: { color: 'automatic' },
            style: {
              color: '#018923',
              backgroundColor: '#2E7D3214',
            },
          },
        ],
      },
      MuiFormHelperText: {
        variants: [
          {
            props: { variant: 'helperError' },
            style: {
              color: '#C62828',
              fontWeight: 400,
              fontSize: '0.75rem',
              letterSpacing: '0.4px',
              lineHeight: '166%',
              marginTop: '0.25rem',
            },
          },
        ],
      },
    },
    MuiPaper: {
      styleOverrides: {
        elevation0: {
          backgroundColor: '#FFFFFF',
          boxShadow: 'none',
        },
        elevation1: {
          backgroundColor: '#FFFFFF',
          boxShadow:
            '0px 1px 3px 0px rgba(0, 0, 0, 0.12), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 2px 1px -1px rgba(0, 0, 0, 0.20)',
        },
        elevation2: {
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 3px 1px -2px rgba(0,0,0,0), 0px 2px 2px 0px rgba(0,0,0,0), 0px 1px 5px 0px rgba(0,0,0,0)',
        },
        elevation3: {
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 3px 3px -2px rgba(0,0,0,0), 0px 3px 4px 0px rgba(0,0,0,0), 0px 1px 8px 0px rgba(0,0,0,0)',
        },
        elevation4: {
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 2px 4px -1px rgba(0,0,0,0), 0px 4px 5px 0px rgba(0,0,0,0), 0px 1px 10px 0px rgba(0,0,0,0)',
        },
        elevation5: {
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 3px 5px -1px rgba(0,0,0,0), 0px 5px 8px 0px rgba(0,0,0,0), 0px 1px 14px 0px rgba(0,0,0,0)',
        },
        elevation6: {
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 3px 5px -1px rgba(0,0,0,0), 0px 6px 10px 0px rgba(0,0,0,0), 0px 1px 18px 0px rgba(0,0,0,0)',
        },
        elevation7: {
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 4px 5px -2px rgba(0,0,0,0), 0px 7px 10px 1px rgba(0,0,0,0), 0px 2px 16px 1px rgba(0,0,0,0)',
        },
        elevation8: {
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 5px 5px -3px rgba(0,0,0,0), 0px 8px 10px 1px rgba(0,0,0,0), 0px 3px 14px 2px rgba(0,0,0,0)',
        },
        elevation9: {
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 5px 6px -3px rgba(0,0,0,0), 0px 9px 12px 1px rgba(0,0,0,0), 0px 3px 16px 2px rgba(0,0,0,0)',
        },
        elevation10: {
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 6px 6px -3px rgba(0,0,0,0), 0px 10px 14px 1px rgba(0,0,0,0), 0px 4px 18px 3px rgba(0,0,0,0)',
        },
        elevation11: {
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 6px 7px -4px rgba(0,0,0,0), 0px 11px 15px 1px rgba(0,0,0,0), 0px 4px 20px 3px rgba(0,0,0,0)',
        },
        elevation12: {
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 7px 8px -4px rgba(0,0,0,0), 0px 12px 17px 2px rgba(0,0,0,0), 0px 5px 22px 4px rgba(0,0,0,0)',
        },
        elevation13: {
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 7px 8px -4px rgba(0,0,0,0), 0px 13px 19px 2px rgba(0,0,0,0), 0px 5px 24px 4px rgba(0,0,0,0)',
        },
        elevation14: {
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 7px 9px -4px rgba(0,0,0,0), 0px 14px 21px 2px rgba(0,0,0,0), 0px 5px 26px 4px rgba(0,0,0,0)',
        },
        elevation15: {
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 8px 9px -5px rgba(0,0,0,0), 0px 15px 22px 2px rgba(0,0,0,0), 0px 6px 28px 5px rgba(0,0,0,0)',
        },
        elevation16: {
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 8px 10px -5px rgba(0,0,0,0), 0px 16px 24px 2px rgba(0,0,0,0), 0px 6px 30px 5px rgba(0,0,0,0)',
        },
        elevation17: {
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 8px 11px -5px rgba(0,0,0,0), 0px 17px 26px 2px rgba(0,0,0,0), 0px 6px 32px 5px rgba(0,0,0,0)',
        },
        elevation18: {
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 9px 11px -5px rgba(0,0,0,0), 0px 18px 28px 2px rgba(0,0,0,0), 0px 7px 34px 6px rgba(0,0,0,0)',
        },
        elevation19: {
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 9px 12px -6px rgba(0,0,0,0), 0px 19px 29px 2px rgba(0,0,0,0), 0px 7px 36px 6px rgba(0,0,0,0)',
        },
        elevation20: {
          backgroundColor: '#FFFFFF',
          boxShadow:
            '0px 10px 13px -6px rgba(0,0,0,0), 0px 20px 31px 3px rgba(0,0,0,0), 0px 8px 38px 7px rgba(0,0,0,0)',
        },
        elevation21: {
          backgroundColor: '#FFFFFF',
          boxShadow:
            '0px 10px 13px -6px rgba(0,0,0,0), 0px 21px 33px 3px rgba(0,0,0,0), 0px 8px 40px 7px rgba(0,0,0,0)',
        },
        elevation22: {
          backgroundColor: '#FFFFFF',
          boxShadow:
            '0px 10px 14px -6px rgba(0,0,0,0), 0px 22px 35px 3px rgba(0,0,0,0), 0px 8px 42px 7px rgba(0,0,0,0)',
        },
        elevation23: {
          backgroundColor: '#FFFFFF',
          boxShadow:
            '0px 11px 14px -7px rgba(0,0,0,0), 0px 23px 36px 3px rgba(0,0,0,0), 0px 9px 44px 8px rgba(0,0,0,0)',
        },
        elevation24: {
          backgroundColor: '#FFFFFF',
          boxShadow:
            '0px 11px 15px -7px rgba(0,0,0,0), 0px 24px 38px 3px rgba(0,0,0,0), 0px 9px 46px 8px rgba(0,0,0,0)',
        },
      },
    },
  },
  ptBR
);

export default ProduttivoMuiTheme;
